import {ioJsonDeleteItemAction, ioJsonFetchAction} from 'Core/store/actions/io';
import {hideLoading, hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {getArray} from 'Core/helpers/data';
import {get, omit} from 'lodash';
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "DataMap/appointment/listItem";
import * as itemFilterDataMap from "DataMap/appointment/filter";
import {reducerStoreKey} from "./reducer";
import {isSuccessful} from "Core/helpers/io";

/**
 * Reset patient record appointments list Redux state to initial state
 * @return {(function(*))|*}
 */
export const resetPatientRecordAppointmentsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
};

/**
 * Fetch patient record appointments list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} patientId - ID of the patient.
 * @param {?string} [therapyId=null] - ID of the therapy.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchPatientRecordAppointmentsListAction = (
	abortCallback, patientId, therapyId = null, filter = null, pageNo = 1, perPage, sortBy, sortDir
) => dispatch => {
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'member/appointments/page-search',
		'',
		{...filter, patientId, therapyId},
		null,
		pageNo,
		perPage,
		sortBy ? sortBy : null,
		sortDir
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: omit(itemFilterDataMap.input(get(responseData, 'filter')), ['patientId', 'therapyId']),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load patient record appointments list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} patientId - ID of the patient.
 * @param {?string} [therapyId=null] - ID of the therapy.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay will be rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadPatientRecordAppointmentsListAction = (
	abortCallback, patientId, therapyId = null, filter = null, pageNo = 1, perPage, sortBy, sortDir,
	loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#patient-record-appointments-table") : null);
	return fetchPatientRecordAppointmentsListAction(
		abortCallback, patientId, therapyId, filter, pageNo, perPage, sortBy, sortDir
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setPatientRecordAppointmentsListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Set patient record appointments list filter in Redux store
 *
 * @param {Object} filter - Filter to set.
 * @return {(function(*): void)|*}
 */
export const setPatientRecordAppointmentsFilterAction = filter => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setPatientRecordAppointmentsListFilter(filter));
};

/**
 * Clear patient record appointments list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearPatientRecordAppointmentsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearPatientRecordAppointmentsListData());
}

/**
 * Delete appointments item action
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - ID of the appointments item that will be deleted.
 * @return {function(*): Promise<IoJsonFetchResponseObject>}
 */
export const deleteAppointmentsItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#delete-appointment-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'member/appointments/delete-by-ids',
		id,
		{},
		hideLoadingFunction(loading)
	)(dispatch);
}

/**
 * Delete multiple appointments items action
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string[]} ids - IDs of the appointments items that will be deleted.
 * @return {function(*): Promise<IoJsonFetchResponseObject>}
 */
export const deleteAppointmentsItemsAction = (abortCallback, ids) => dispatch => {
	const loading = showLoading('#delete-appointment-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'member/appointments/delete-by-ids',
		ids,
		{},
		hideLoadingFunction(loading)
	)(dispatch);
}
